import React, { createContext, useContext, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AccountData } from '../api';

const LoaderLogoContext = createContext<string | undefined>(undefined);

const storageKey = (accountId: string) => `loaderLogo:${accountId}`;

type LoaderLogoProviderProps = {
  account: Pick<AccountData, 'id' | 'logo'> | undefined | null;
  children: React.ReactNode;
};

export const LoaderLogoProvider = ({ account, children }: LoaderLogoProviderProps) => {
  // Remembered per account so the first loader, shown before the account is fetched, can use it next visit.
  useEffect(() => {
    if (!account?.id) return;
    try {
      if (account.logo) localStorage.setItem(storageKey(account.id), account.logo);
      else localStorage.removeItem(storageKey(account.id));
    } catch {
      // storage may be blocked (e.g. third-party iframe)
    }
  }, [account?.id, account?.logo]);

  return <LoaderLogoContext.Provider value={account?.logo || undefined}>{children}</LoaderLogoContext.Provider>;
};

export const useLoaderLogo = () => {
  const contextLogo = useContext(LoaderLogoContext);
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  if (contextLogo) return contextLogo;
  const id = accountId || searchParams.get('account_id');
  if (!id) return undefined;
  try {
    return localStorage.getItem(storageKey(id)) || undefined;
  } catch {
    return undefined;
  }
};
